import { memo, useEffect, useMemo } from 'react';
import { useCustomTranslation } from '../../../../hooks';
import {
  closeTaskDetailsDrawer,
  openTaskDetailsDrawer,
} from '../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';
import { EmptyStatePage } from '../../EmptyStatePage';
import { SimulationRange } from '../GanttSimulation/components';
import { SimulationRangeTime } from '../GanttSimulation/components/SimulationRangeTime';
import { ResetButton } from '../components';
import { GanttMode, useProductionPlanQueryParams } from '../hooks';
import { useGanttFilterStore } from '../hooks/useGanttFilterStore';
import { type QueueStored, useGroupedQueuesStore, visibleQueuesSelector } from '../store';
import { GanttConfig } from './GanttConfig';
import { GanttQueueLabel, type QueueWithStatus } from './GanttQueueLabel';
import { GanttDependenciesGraph } from './components/GanttDependenciesGraph';
import { GanttQueuesRenderer } from './components/GanttQueuesRenderer';
import { GanttQueuesSkeletonRenderer } from './components/GanttQueuesSkeletonRenderer';
import { Timeline } from './components/Timeline';
import { TimelineFilterChip } from './components/Timeline/TimelineFilterChip';
import { TimeMarkerLine } from './components/Timeline/components';
import { TimeMarkerTime } from './components/Timeline/components/TimeMarkerTime';
import { LocateProductionTask } from './hooks';
import { selectGanttInstanceData, useGanttStore, useTimelineStore } from './store';
import type { GanttQueue } from './types';
import { generateQueues, useGanttInstanceId } from './utils';

type GanttQueuesWithStatus = { orderQueueWithStatus: Array<QueueWithStatus>; ganttQueues: GanttQueue[] };

export const GanttComponent = memo(() => {
  const { queryParams } = useProductionPlanQueryParams();
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const { isLoading, ganttQueues, orderQueueWithStatus } = useGanttData();

  const { from, to } = useGanttFilterStore((state) => state.ganttDateRange);

  useEffect(() => {
    if (queryParams.taskId && queryParams.mode !== GanttMode.WORKER) {
      setTimeout(() => {
        openTaskDetailsDrawer({
          taskId: queryParams?.taskId?.toString() || '',
        });
      }, 0);
    }

    return () => {
      closeTaskDetailsDrawer();
    };
    // we need open drawer only once page opened with taskId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isLoading && ganttQueues.length === 0) {
    return (
      <EmptyStatePage
        Actions={<ResetButton className="shrink-0" variant="outlined" />}
        text={tWrapper('noSearchResults')}
      />
    );
  }

  return (
    <div className="scroll-stable h-full overflow-auto overflow-y-scroll" id={LocateProductionTask.scrollContainerId}>
      <div className="flex h-fit w-fit min-w-full">
        <div className="sticky left-0 z-13">
          <div className="sticky top-0 z-15 box-border flex h-16.75 min-w-32 items-center border-b border-border-primary bg-surface-primary">
            <TimelineFilterChip />
          </div>
          <div className="border-r border-r-border-primary bg-surface-primary">
            {orderQueueWithStatus.map(({ id, name, shortName, area, status, active }) => (
              <GanttQueueLabel
                active={active}
                areaHex={area.hex}
                areaName={area.name}
                key={id}
                name={name}
                shortName={shortName}
                status={status}
              />
            ))}
          </div>
        </div>
        <div data-gantt-deps-origin className="relative z-12 h-fit grow">
          <TimeMarkerLine endDate={to} startDate={from} />
          <SimulationRange startDate={from} />
          <div className="sticky top-0 z-11 border-b border-border-primary bg-surface-primary">
            <Timeline endDate={to} startDate={from} />
          </div>
          <div className="sticky top-17 z-6000">
            <TimeMarkerTime endDate={to} startDate={from} />
            <SimulationRangeTime startDate={from} />
          </div>
          <div>
            {isLoading ? (
              <GanttQueuesSkeletonRenderer from={from} ganttQueues={ganttQueues} to={to} />
            ) : (
              <GanttQueuesRenderer ganttQueues={ganttQueues} />
            )}
            <GanttDependenciesGraph ganttQueues={ganttQueues} />
          </div>
        </div>
      </div>
    </div>
  );
});

const EMPTY_ARRAY: QueueStored[] = [];

const useGanttData = () => {
  const instanceId = useGanttInstanceId();
  const orderQueue = useGroupedQueuesStore(visibleQueuesSelector);
  const { queues, isLoading } = useGanttStore(selectGanttInstanceData(instanceId));
  const { from, to } = useGanttFilterStore((state) => state.ganttDateRange);
  const currentFilter = useTimelineStore((state) => state.filters.current);

  const minNodeWithForMonthsScale =
    currentFilter.measureType === 'months' ? GanttConfig.SINGLE_ELEMENT_WIDTH : undefined;

  const filterOrderQueue = useMemo(() => {
    return queues.length > 0
      ? orderQueue.filter(({ id }) => queues.some((q) => q.id === id))
      : isLoading
        ? orderQueue
        : EMPTY_ARRAY;
  }, [orderQueue, queues, isLoading]);

  const { ganttQueues, orderQueueWithStatus } = useMemo(() => {
    return filterOrderQueue.reduce(
      (acc, q) => {
        const ganttQueue = queues.find((_) => _.id === q.id);
        acc.orderQueueWithStatus.push({ ...q, status: ganttQueue?.status });

        // add load before offset to display full element eg. one day
        // const fromWithOffset = addHours(from, -currentFilter.measureScale);
        if (ganttQueue) {
          acc.ganttQueues.push(
            generateQueues(ganttQueue, from, to, currentFilter, minNodeWithForMonthsScale) as GanttQueue,
          );
        } else {
          // add empty queue to display, when data is loading
          acc.ganttQueues.push({
            id: q.id || 0,
            shortName: q.shortName,
            name: q.name,
            area: q.area,
            status: 'green', // doesn't matter
            queueNodes: [],
          });
        }
        return acc;
      },
      { orderQueueWithStatus: [], ganttQueues: [] } as GanttQueuesWithStatus,
    );
  }, [currentFilter, from, filterOrderQueue, queues, to, minNodeWithForMonthsScale]);

  return { ganttQueues, isLoading, orderQueueWithStatus };
};

GanttComponent.displayName = 'Gantt';
